import { ReactElement, useCallback } from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	FontIconName,
	TreeAccordion
} from "client/Components";
import { AppCategory } from "client/Api/Apps/common";
import { DataSourceSortDirection, nativeSortFunction } from "client/Data/sorting";
import { useToggleExpansion } from "client/Components/TreeAccordion/useToggleExpansion";
import { getNumericEnumValues } from "client/Misc/utils";

const CATEGORY_ITEM_ID: string = "category";

export type CategoryFilterProps = {
	items: readonly CategoryEntry[];
	value: CategoryEntry | null;
	onChange(entry: CategoryEntry): void;
	hasSectionHeader?: boolean;
};

export const CategoryFilter = (props: CategoryFilterProps): ReactElement => {
	const {
		items,
		value,
		onChange,
		hasSectionHeader
	} = props;

	const { isExpanded, toggleExpansion } = useToggleExpansion([CATEGORY_ITEM_ID]);

	const toggleExpansionHandler = useCallback(
		(): void => toggleExpansion(CATEGORY_ITEM_ID),
		[toggleExpansion]
	);

	const renderItems = (indentionLevel: number): ReactElement => (
		<>
			{items.map((x) => (
				<TreeAccordion.Node key={x.id}>
					<TreeAccordion.Item
						isSelected={value?.id === x.id}
						icon={x.id === null ? "cloud" : categoryToIconMap[x.id]}
						onClick={() => onChange(x)}
						trackingId={x.id?.toString() ?? "all"}
						indentionLevel={indentionLevel}
						title={x.name}
					>
						{x.name}
					</TreeAccordion.Item>
				</TreeAccordion.Node>
			))}
		</>
	);

	return (
		<TreeAccordion trackingId="app-category-filter">
			{hasSectionHeader
				? (
					<TreeAccordion.Node>
						<TreeAccordion.Item
							trackingId="app-category-filter__header"
							expansionToggle="visible"
							isExpanded={isExpanded(CATEGORY_ITEM_ID)}
							onToggleExpansion={toggleExpansionHandler}
							onClick={toggleExpansionHandler}
						>
							{localize("_Category")}
						</TreeAccordion.Item>
						<TreeAccordion.SubTree
							isCollapsed={!isExpanded(CATEGORY_ITEM_ID)}
						>
							{renderItems(1)}
						</TreeAccordion.SubTree>
					</TreeAccordion.Node>
				)
				: renderItems(0)
			}
		</TreeAccordion>
	);
};
CategoryFilter.displayName = "CategoryFilter";

const categoryToIconMap: { [key in AppCategory]: FontIconName } = {
	[AppCategory.Productivity]: "gear",
	[AppCategory.Office]: "buildings",
	[AppCategory.Communication]: "bullhorn",
	[AppCategory.Web]: "globe",
	[AppCategory.Social]: "user-group",
	[AppCategory.Entertainment]: "masks-theater",
	[AppCategory.News]: "newspaper",
	[AppCategory.Finance]: "credit-card",
	[AppCategory.Education]: "graduation-cap",
	[AppCategory.Medical]: "briefcase-medical",
	[AppCategory.Other]: "compass",
	[AppCategory.Custom]: "wrench"
};

export const categoryToNameMap: { [key in AppCategory]: LocalizationKey; } = {
	[AppCategory.Productivity]: "_Productivity",
	[AppCategory.Office]: "_Office",
	[AppCategory.Communication]: "_Communication",
	[AppCategory.Web]: "_Web",
	[AppCategory.Social]: "_Social",
	[AppCategory.Entertainment]: "_Entertainment",
	[AppCategory.News]: "_News",
	[AppCategory.Finance]: "_Finance",
	[AppCategory.Education]: "_Education",
	[AppCategory.Medical]: "_Medical",
	[AppCategory.Other]: "_Other",
	[AppCategory.Custom]: "_Custom",
};

export type CategoryEntry = {
	id: AppCategory | null;
	name: string;
};

export const allAppsCategory: CategoryEntry = {
	id: null,
	name: localize("_AllApps")
};

export const categories: CategoryEntry[] = (() => {
	const realCategories: CategoryEntry[] = getNumericEnumValues(AppCategory)
		.map(x => {
			const result: CategoryEntry = {
				id: x,
				name: localize(categoryToNameMap[x])
			};

			return result;
		})
		.sort((a, b) => {
			if (a.id === AppCategory.Other && b.id === AppCategory.Other) {
				return 0;
			}

			if (a.id === AppCategory.Other) {
				return 1;
			}

			if (b.id === AppCategory.Other) {
				return -1;
			}

			return nativeSortFunction(a.name, b.name, DataSourceSortDirection.Ascending);
		});

	return [
		allAppsCategory,
		...realCategories
	];
})();

export function getCategoryName(category: AppCategory): string {
	return localize(categoryToNameMap[category]);
}

export const hiddenCategoryIds: readonly AppCategory[] = [
	AppCategory.Medical,
	AppCategory.News,
	AppCategory.Finance,
	AppCategory.Education
];
